import React from 'react'
// libs
import styled, { css } from 'styled-components'
import { Field } from 'redux-form'
import { useQuery } from '@apollo/client/react'
// components
import { Avatar as AvatarOrigin } from '@mui/material'
// hooks
import { useCurrentUser } from 'hooks'
// graphql
import { COMPANY_DAYS_QUERY } from 'graphql/company/queries'
// themes
import { colors } from 'themes'
// constants
import { BUSINESS_DAYS_OPTIONS } from 'constants/date'
// types
import { Validators } from 'utils/validators'
import { WrappedFieldProps } from 'redux-form/lib/Field'
// utils
import moment from 'moment'
import { get } from 'lodash'
import { safeGetOr } from 'utils/fp'
import { map, pipe, sortBy } from 'lodash/fp'

const Avatar = styled(({ isSelected, ...props }) => <AvatarOrigin {...props} />)`
  && {
    cursor: pointer;
    color: ${colors.gray850};
    background: ${colors.gray210};

    ${({ isSelected }) =>
      isSelected &&
      css`
        color: ${colors.white};
        background: ${colors.blue500};
      `}
  }
`

const WeekDaysContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 2;
`

const DaysContainer = styled.div`
  gap: 8px;
  display: flex;
  margin-top: 8px;
`

const getSortedDays = sortBy((v: any) => moment().day(v).isoWeekday())

interface Props {
  name: string
  validate?: Validators
}

const Component: React.FC<Props & WrappedFieldProps> = ({ input }) => {
  const user = useCurrentUser()

  const { data: daysQuery } = useQuery(COMPANY_DAYS_QUERY, {
    skip: !user?.companyId,
    variables: {
      filter: {
        id: user?.companyId
      }
    }
  })

  const inputValue = input.value || []
  const days = pipe(
    safeGetOr<any, any, any>([], 'companies.data[0].businessDays'),
    map('key'),
    getSortedDays
  )(daysQuery)
  const companyBusinessDays = get(daysQuery, 'companies.data[0].businessDays', [])

  const options = companyBusinessDays
    ? BUSINESS_DAYS_OPTIONS
    : BUSINESS_DAYS_OPTIONS.filter(({ value }) => days.includes(value))

  const handleDayClick =
    ({ value }) =>
    (_event: React.MouseEvent) => {
      let result = [...inputValue]

      if (result.includes(value)) {
        result = result.filter((v) => v !== value)
      } else {
        result.push(value)
      }

      result = getSortedDays(result)
      input.onChange(result)
    }

  return (
    <WeekDaysContainer>
      <span>Period commences on day(s)</span>
      <DaysContainer>
        {options.map(({ label, value }) => (
          <Avatar
            key={value}
            data-testid='week-day-option'
            data-entity-id={value}
            isSelected={inputValue.includes(value)}
            onClick={handleDayClick({ value })}>
            {label[0]}
          </Avatar>
        ))}
      </DaysContainer>
    </WeekDaysContainer>
  )
}

const WeekDaysReduxSelect: React.FC<Props> = (props) => <Field {...props} component={Component} />

export default WeekDaysReduxSelect
